<template>
  <div>
    <div class="d-flex flex-column flex-lg-row align-items-start">
      <div class="mb-3 pr-lg-4">
        <strong class="mb-3 d-block">{{ $t("Calendar") }}</strong>
        <DatePicker
          :min-date="dayjs().toISOString()"
          :max-date="dayjs().add(3, 'year').toISOString()"
          inline
          :allowed-dates="allowedDates"
          :loading="datePickerIsLoading"
          @update-month-year="handleMonthYear"
          :model-value="date.toString()"
          @update:model-value="updateDate"
        ></DatePicker>
      </div>
      <div class="flex-grow-1 w-100">
        <div :class="[
          'mb-3 card',
          !useSlotType ? 'bg-light' : '',
        ]">
          <div class="card-body">
            <div class="form-check mb-3">
              <input class="form-check-input" type="radio" name="radioTypeDate" id="radioSlot" :checked="useSlotType" @change="useSlotType = true">
              <label class="form-check-label font-weight-bold text-dark" for="radioSlot">
                {{ $t("Slot") }} :
              </label>
            </div>
            <div class="form-group">
              <SlotSelector
                :slots="allowedSlots"
                :selectedSlot="selectedSlot"
                :disabled="!useSlotType"
                @slot-change="onSlotChange"
              ></SlotSelector>
            </div>
          </div>
        </div>

        <div v-if="booking?.store && booking?.start_at" :class="[
         'card',
          useSlotType ? 'bg-light' : '',
        ]">
          <div class="card-body">
            <div class="form-check mb-3">
              <input class="form-check-input" type="radio" name="radioTypeDate" id="radioFreeDate" :checked="!useSlotType" @change="useSlotType = false">
              <label class="form-check-label font-weight-bold text-dark" for="radioFreeDate">
                {{ $t("Free date") }} :
              </label>
            </div>
            <fieldset :disabled="useSlotType">
              <div class="form-group">
                <label for="storeFreeDateStartAt">
                  {{ $t("Start at:") }}
                </label>
                <Datetime
                  id="storeFreeDateStartAt"
                  :min-date="minDate"
                  :max-date="maxDate"
                  v-model="startAt"
                ></Datetime>
              </div>
              <div class="form-group">
                <label for="storeFreeDateEndAt">
                  {{ $t("End at:") }}
                </label>
                <Datetime
                  id="storeFreeDateEndAt"
                  :min-date="minDate"
                  :max-date="maxDate"
                  v-model="endAt"
                ></Datetime>
              </div>
            </fieldset>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import dayjs from "dayjs";
import SlotSelector from "@/components/booking/StoreSlotSelector.vue";
import { useNotification } from "@kyvg/vue3-notification";
import DatePicker from "@/components/ui/DatePicker.vue";
import Datetime from "@/components/ui/Datetime.vue";
import useBookingStoreSlotAvailability from "@/composables/useBookingStoreSlotAvailability.ts";

const { notify } = useNotification();

const { booking, apiUrl } = defineProps({
  booking: {
    type: Object,
    default: null
  },
  apiUrl: {
    type: String,
    default: null
  }
});

const emit = defineEmits([
  "change-store-slot"
]);

const minDate = dayjs().toISOString();
const maxDate = dayjs().add(3, "year").toISOString();

const selectedStore = ref(booking?.store?.id ?? null);
const startAt = ref(booking?.start_at ? dayjs(booking.start_at) : null);
const endAt = ref(booking?.end_at ? dayjs(booking.end_at) : null);
const date = ref(startAt.value ? startAt.value.toISOString() : dayjs().toISOString());
const selectedSlot = computed(() => {
  return {
    start_at: startAt.value,
    end_at: endAt.value
  }
});
const useSlotType = ref(true);

const disabled = ref(!!booking?.store?.id);
const allowedDates = ref([]);

const month = ref(null);
const year = ref(null);
const allowedSlots = ref([]);

const updateDate = (value) => {
  if(dayjs(value).isSame(dayjs(date.value), 'day')) {
    return;
  }
  date.value = dayjs(value);
  startAt.value = null;
  endAt.value = null;
};

const onSlotChange = (slot) => {
  startAt.value = slot.start_at;
  endAt.value = slot.end_at;
};

const handleMonthYear = ({ month: m, year: y }) => {
  month.value = m + 1;
  year.value = y;
  fetchCalendar();
};

const {
  isLoading: datePickerIsLoading,
  getAllowedDates,
  getAvailableSlots,
} = useBookingStoreSlotAvailability(
  apiUrl,
  window.Laravel.locale,
)

const fetchCalendar = async () => {
  if (!selectedStore.value || !booking.equipment?.product?.id) {
    return;
  }
  if (year.value === null || month.value === null) {
    year.value = dayjs(date.value).year();
    month.value = dayjs(date.value).month() + 1;
  }

  try {
    allowedDates.value =  await getAllowedDates(
      booking.equipment.product.id,
      selectedStore.value,
      booking.equipment.size,
      booking.equipment.color,
      year.value,
      month.value,
    )
  } catch (error) {
    notify({
      group: "errors",
      type: "error",
      title: error.message
    });
    allowedDates.value = [];
  }
  if(!booking?.start_at) {
    const firstAvailableDate = allowedDates.value.find((date) => dayjs(date).isAfter(dayjs().startOf('date')));
    date.value = firstAvailableDate ? dayjs(firstAvailableDate) : dayjs().toISOString();
  }
};

const fetchSlot = async () => {
  if (!booking.equipment) {
    return;
  }

  try {
    allowedSlots.value = await getAvailableSlots(
      booking.equipment.product.id,
      selectedStore.value,
      booking.equipment.size,
      booking.equipment.color,
      year.value,
      month.value,
      dayjs(date.value).date()
    )
  } catch (error) {
    allowedSlots.value = [];
    notify({
      group: "errors",
      type: "error",
      title: error.message
    });
  }

  if(startAt.value && endAt.value) {
    useSlotType.value = allowedSlots.value.filter((slot) =>
      slot.start_at === dayjs(selectedSlot.value.start_at).utc().local().format()
      && slot.end_at === dayjs(selectedSlot.value.end_at).utc().local().format()
    ).length > 0;
  }
};

watch(() => booking.equipment, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    fetchCalendar();
    fetchSlot();
  }
});

watch(() => booking.store, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    selectedStore.value = newValue.id;
  }
});

watch(selectedStore, () => {
  allowedSlots.value = [];
  fetchCalendar();
});

watch(date, (newValue, oldValue) => {
  if (dayjs(newValue).isSame(dayjs(oldValue))) {
    return;
  }
  fetchSlot();
});

watch(selectedSlot, () => {
  if (selectedSlot.value && selectedSlot.value?.start_at && selectedSlot.value?.end_at) {
    emit("change-store-slot", {
      start_at: selectedSlot.value.start_at,
      end_at: selectedSlot.value.end_at
    });
  }
});

onMounted(() => {
  fetchCalendar();
  fetchSlot()
});
</script>